/* Track the last time the label has "adjusted", so observers know when it's safe to capture
 * a snapshot. The label is not considered settled until:
 *   1. The "load" event has fired (all initial CSS/JS/image resources fetched)
 *   2. document.fonts.ready has resolved (all label fonts loaded)
 *   3. All <img> elements have decoded to a paintable state
 *   4. A mutation-free `settleTime` window has passed since the last DOM change
 *
 * The lack of window.recipeLabelSettled is an implicit false.
 */

import { isNil } from "lodash-es";

const targetSelector = "#label-and-claim-div";

/* No change to the DOM in settleTime ms means we're ready */
const settleTime = 100.0;

/* Watchdog: Always consider the label loaded after maxWait ms */
const maxWait = 3000.0;

let isWaiting = false;
let lastModification = null;
let forceSignal = false;
let resourcesReady = false;

export const readySignal = () => {
  isWaiting = true;
  lastModification = window.performance.now();
};

/* Specifically want "load" here: We need CSS and JS */
window.addEventListener("load", async () => {
  const mutationObserver = new MutationObserver(() => {
    lastModification = window.performance.now();
  });

  mutationObserver.observe(document.querySelector(targetSelector), {
    attributes: true,
    childList: true,
    subtree: true,
  });

  /* Wait for fonts. Label rendering is font-metric sensitive — a font that
   * loads a few frames late can shift hairline separators by a subpixel,
   * breaking the pixel-diff specs.
   */
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* best-effort */
    }
  }

  /* Wait for every <img> to be decoded to a paintable state. For images that
   * are already complete this resolves immediately. This is a belt-and-braces
   * guard against capturing a screenshot mid-decode.
   */
  await Promise.all(Array.from(document.images).map((img) => img.decode().catch(() => {})));

  /* Start the settleTime window from a known-good "everything is loaded"
   * moment so callers don't get a settled=true reading that predates the
   * load event.
   */
  lastModification = window.performance.now();
  resourcesReady = true;
});

/* Set the watchdog to enforce maxWait */
window.addEventListener("DOMContentLoaded", () => {
  setTimeout(() => {
    forceSignal = true;
  }, maxWait);
});

window.recipeLabelSettled = () => {
  if (forceSignal) {
    return true;
  }

  if (isWaiting && resourcesReady && !isNil(lastModification)) {
    return window.performance.now() - lastModification > settleTime;
  }

  return false;
};
